import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import i18n from '../i18n';

interface ApiMutationOptions<TData, TVariables> {
  onSuccess?: (data: TData) => void;
  onError?: (error: Error) => void;
  invalidateQueries?: string[];
  showSuccessToast?: {
    title: string;
    message?: string;
  };
  showErrorToast?: {
    title: string;
    customMessage?: string; // If provided, this overrides the auto-generated error message
  };
}

export const useApiMutation = <TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: ApiMutationOptions<TData, TVariables>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      // Invalidate specified queries
      if (options?.invalidateQueries) {
        options.invalidateQueries.forEach(queryKey => {
          queryClient.invalidateQueries({ queryKey: [queryKey] });
        });
      }

      // Call custom success handler
      options?.onSuccess?.(data);
    },
    onError: (error: AxiosError, variables) => {
      console.error('API mutation failed:', error);
      
      // Call custom error handler
      options?.onError?.(error as Error);
    },
  });
};

// System admins have no tenant/organization of their own, so writes outside the
// Admin Console fail server-side with a generic 500. Detect that case from the
// JWT role rather than the error body, since the backend response is indistinguishable
// from any other unhandled error.
const isSystemAdminNoTenantFailure = (error: AxiosError): boolean => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const { groups } = jwtDecode<{ groups?: string[] }>(token);
    if (!groups?.includes('SUPER_ADMIN')) return false;

    const url = error.config?.url ?? '';
    const method = (error.config?.method ?? '').toLowerCase();
    const status = error.response?.status;

    const isAdminEndpoint = url.startsWith('/admin');
    const isWrite = method !== 'get';
    const isServerFailure = status === undefined || status >= 500;

    return !isAdminEndpoint && isWrite && isServerFailure;
  } catch {
    return false;
  }
};

export const getApiErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    if (isSystemAdminNoTenantFailure(error)) {
      return i18n.t('errors.systemAdminNoTenant', { ns: 'common' });
    }

    // Handle specific HTTP status codes
    switch (error.response?.status) {
      case 400:
        return 'Invalid request data. Please check all required fields.';
      case 401:
        return 'You are not authorized to perform this action.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'This action conflicts with existing data.';
      case 422:
        return 'Data validation failed. Please check your input.';
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return error.response?.data?.message || 'An unexpected error occurred. Please try again.';
    }
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};
